/**
 * Générateur — Attendu 966 — « Utiliser une propriété caractéristique sur les
 * diagonales ou les côtés pour les construire ou donner la nature du
 * quadrilatère. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Parallélogrammes
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/966.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Les DEUX usages que le libellé énumère sont servis (`usages_prevus`) :
 *  donner la nature (cartes 1 à 3) et construire (cartes 4 à 7), et chaque
 *  construction s'accompagne d'une question de nature ou de propriété
 *  (`ecarts_assumes` 1 et 4). Une seule propriété caractéristique est mobilisée
 *  par question (`nombre_de_proprietes_mobilisees_par_question`).
 *
 *  1. codage         — niveau 1 : la nature la plus précise que le CODAGE d'une
 *                      figure à main levée permet d'affirmer.
 *  2. rectangle_diag — niveau 2 : la même décision à partir d'un énoncé de
 *                      propriétés, sans figure — oui, non, ou on ne peut pas
 *                      conclure.
 *  3. suffisant      — niveau 3 : décider qu'un codage ne suffit pas, et
 *                      désigner le contre-exemple DESSINÉ qui le montre.
 *  4. construire_diag— niveau 4 : construire à partir des deux diagonales et de
 *                      leur angle ; le reliage nomme la propriété employée.
 *  5. cotes_angle    — niveau 5 : deux côtés et l'angle au sommet.
 *  6. demi_diagonales— niveau 6 : un côté et les deux demi-diagonales.
 *  7. alternes       — niveau 7 : une diagonale et deux angles, par les angles
 *                      alternes internes (`ecarts_assumes` 5).
 *  8. dole           — niveau 8 : la configuration du cadrage, construire puis
 *                      conclure.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - longueur d'une diagonale : décimal à une décimale de 7 à 9 cm, pour LES
 *     DEUX diagonales ;
 *   - rapport des deux diagonales : de 1,2 à 1,286 (voir Non tranché), jamais 1 ;
 *   - angle entre les deux diagonales : entier de 80 à 100 degrés, jamais 89,
 *     90 ni 91 ;
 *   - angle au sommet : entier de 95 à 120 degrés, toujours obtus ;
 *   - angles avec la diagonale : entiers de 25 à 45 degrés, distants d'au moins
 *     5 degrés ;
 *   - quadrilatères proposés en identification : 4, plus « on ne peut pas
 *     conclure » quand des configurations insuffisantes sont engendrées ;
 *   - configurations insuffisantes : 4 sur les 10 du crible ;
 *   - lettres O, Q et X écartées du nommage ; O est le centre, X les
 *     demi-droites de construction.
 *  Exclusions : aucun parallélogramme particulier produit par hasard en
 *   construction (l'angle des diagonales n'est jamais droit, les diagonales
 *   jamais de même longueur, les deux angles avec une diagonale jamais égaux,
 *   les deux côtés jamais égaux) ; la nature est décidée À LA GÉNÉRATION, à
 *   partir des paramètres, jamais a posteriori en comparant des flottants
 *   (exclusion 2, vigilance 2) ; aucun quadrilatère croisé ; figures
 *   d'identification à main levée, jamais en vraie grandeur ; la réponse « on
 *   ne peut pas conclure » est toujours offerte quand elle est attendue.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. conclure au losange parce que les diagonales PARAISSENT perpendiculaires
 *      → `perpendiculaire_a_vue` (codage, rectangle_diag ; l'angle des
 *        diagonales est entre 80 et 100 degrés sans jamais être droit)
 *   2. répondre plus particulier que ce que les données permettent
 *      → `trop_particulier` (codage, rectangle_diag, construire_diag, dole)
 *   3. s'arrêter à « parallélogramme » quand les données donnent mieux
 *      → `pas_assez_particulier` (codage, rectangle_diag, construire_diag)
 *   4. croire qu'un codage insuffisant établit que la figure n'est PAS un
 *      parallélogramme → `insuffisant_pris_pour_faux` (codage, suffisant,
 *      rectangle_diag ; la proposition dit « on ne peut pas conclure »,
 *      jamais « ce n'est pas un parallélogramme », vigilance 1)
 *   5. confondre côtés consécutifs et côtés opposés
 *      → `cotes_consecutifs` (suffisant, codage)
 *   6. reporter une demi-diagonale comme si c'était la diagonale entière
 *      → `demi_diagonale_pour_entiere` (`erreursVisees` des constructions par
 *        les diagonales, dont les consignes disent la moitié)
 *   7. croire qu'un seul parallélisme ou une seule égalité suffit
 *      → `une_seule_egalite` (suffisant, codage)
 *   8. construire d'abord et qualifier ensuite
 *      → `construire_sans_qualifier` (`erreursVisees` des constructions, dont
 *        chacune pose la question de la nature)
 *
 *  Graduation → cartes : 1 codage · 2 rectangle_diag · 3 suffisant ·
 *   4 construire_diag · 5 cotes_angle · 6 demi_diagonales · 7 alternes ·
 *   8 dole et `generate()` sans clé.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `rapport_des_deux_diagonales` monte à 1,8 alors que
 *     `longueur_d_une_diagonale` borne les DEUX diagonales entre 7 et 9 cm :
 *     le rapport ne peut alors pas dépasser 9 ÷ 7 ≈ 1,286. Les deux variables
 *     sont appliquées ensemble, le rapport plafonne donc à 1,286.
 *   - Aucune variable ne borne la longueur d'un CÔTÉ ; les niveaux 5 et 6 en
 *     ont besoin. Appliqué : entier de 4 à 8 cm, comme l'objectif voisin 964.
 *   - La grille d'auto-contrôle 5 × 5 n'a pas de format dans l'application :
 *     le régime servi est la construction, de tolérance 12 px (0,4 carreau).
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    basePayload, makeQcm, makeRelier, makeTexteTrou, makeProgrammeConstruction,
    filtrerDistracteurs,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 966;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_CODAGE = 'Lire la nature dans le codage';
const L_RECT = 'Est-ce un rectangle ?';
const L_SUFFISANT = 'Ce codage suffit-il ?';
const L_DIAG = 'Construire par les diagonales';
const L_COTES = 'Construire par deux côtés et l\'angle';
const L_DEMI = 'Construire par les demi-diagonales';
const L_ALTERNES = 'Construire par une diagonale et deux angles';
const L_DOLE = 'La configuration DOLE';

const DIAG_MIN = 7;
const DIAG_MAX = 9;
const RAPPORT_MIN = 1.2;
const ANGLE_DIAG_MIN = 80;
const ANGLE_DIAG_MAX = 100;
const ANGLE_SOMMET_MIN = 95;
const ANGLE_SOMMET_MAX = 120;
const ANGLE_DIAG_COTE_MIN = 25;
const ANGLE_DIAG_COTE_MAX = 45;
const COTE_MIN = 4;
const COTE_MAX = 8;

const PX = 30;
const TOL = 12;
const TOL_TXT = '0,4 carreau';
const PAD = 56;
const COL_B = '#2563EB';
const COL_V = '#1D9E75';

const toRad = (d) => (d * Math.PI) / 180;
// Les lettres O, Q et X sont écartées du nommage (exclusion 4).
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const NOM_CENTRE = 'O';
const fr = (x) => String(Math.round(x * 10) / 10).replace('.', ',');

const E_PERP_VUE = 'perpendiculaire_a_vue';
const E_TROP = 'trop_particulier';
const E_PAS_ASSEZ = 'pas_assez_particulier';
const E_INSUFFISANT = 'insuffisant_pris_pour_faux';
const E_CONSECUTIFS = 'cotes_consecutifs';
const E_DEMI_DIAG = 'demi_diagonale_pour_entiere';
const E_UNE_EGALITE = 'une_seule_egalite';
const E_SANS_QUALIFIER = 'construire_sans_qualifier';

const CONCLURE_IMPOSSIBLE = 'On ne peut pas conclure : ce codage ne suffit pas à l\'affirmer.';

/**
 * Couples de diagonales respectant EN MÊME TEMPS `longueur_d_une_diagonale`
 * (7 à 9 cm, une décimale) et `rapport_des_deux_diagonales` (au moins 1,2).
 * Voir Non tranché : le rapport plafonne alors à 9 ÷ 7.
 */
const COUPLES_DIAGONALES = (() => {
    const out = [];
    for (let a = DIAG_MIN * 10; a <= DIAG_MAX * 10; a++) {
        for (let b = DIAG_MIN * 10; b < a; b++) {
            const r = a / b;
            if (r >= RAPPORT_MIN - 1e-9) out.push([a / 10, b / 10, Math.round(r * 1000) / 1000]);
        }
    }
    return out;
})();

/** Angle entre les diagonales : proche du droit sans l'être (exclusion 1). */
const tirerAngleDiagonales = () => {
    let a = randInt(ANGLE_DIAG_MIN, ANGLE_DIAG_MAX);
    for (let i = 0; i < 40 && [89, 90, 91].includes(a); i++) a = randInt(ANGLE_DIAG_MIN, ANGLE_DIAG_MAX);
    return [89, 90, 91].includes(a) ? 82 : a;
};

/** Deux angles distincts avec la diagonale, distants d'au moins 5 degrés. */
const tirerAnglesDiagonale = () => {
    for (let i = 0; i < 200; i++) {
        const a = randInt(ANGLE_DIAG_COTE_MIN, ANGLE_DIAG_COTE_MAX);
        const b = randInt(ANGLE_DIAG_COTE_MIN, ANGLE_DIAG_COTE_MAX);
        if (Math.abs(a - b) >= 5) return [a, b];
    }
    return [28, 40];
};

/** Deux côtés consécutifs distincts (jamais un losange). */
const tirerCotes = () => {
    for (let i = 0; i < 200; i++) {
        const a = randInt(COTE_MIN, COTE_MAX);
        const b = randInt(COTE_MIN, COTE_MAX);
        if (Math.abs(a - b) >= 1) return [a, b];
    }
    return [7, 5];
};

// ═══════════════════════════════════════════════════════════
// ASSEMBLAGE D'UN PROGRAMME DE CONSTRUCTION
// ═══════════════════════════════════════════════════════════

const assembler = ({ points, preShapes, tasks, texte, label, key, params }) => {
    const xs = [];
    const ys = [];
    const noter = (p) => { xs.push(p.x); ys.push(p.y); };
    points.forEach(noter);
    tasks.forEach((t) => {
        const s = t.shape;
        if (s.type === 'segment' || s.type === 'line') { noter(s.p1); noter(s.p2); }
        else if (s.type !== 'ray') { xs.push(s.cx - s.r, s.cx + s.r); ys.push(s.cy - s.r, s.cy + s.r); }
        (t.extraShapes || []).forEach((e) => { noter(e.p1); noter(e.p2); });
    });
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const dx = PAD - minX;
    const dy = PAD - minY;
    const vb = {
        w: Math.round(Math.max(...xs) - minX + 2 * PAD),
        h: Math.round(Math.max(...ys) - minY + 2 * PAD),
    };
    const a2 = (v) => Math.round(v * 100) / 100;
    const mv = (p) => ({ ...p, x: a2(p.x + dx), y: a2(p.y + dy) });
    const mvShape = (s) => {
        if (s.type === 'segment' || s.type === 'line') return { ...s, p1: mv(s.p1), p2: mv(s.p2) };
        if (s.type === 'ray') return s;
        return { ...s, cx: a2(s.cx + dx), cy: a2(s.cy + dy) };
    };
    const payload = makeProgrammeConstruction({
        pxPerCm: PX,
        tolerance: TOL,
        viewBox: vb,
        points: points.map(mv),
        preShapes: preShapes || [],
        tasks: tasks.map((t) => ({
            ...t,
            shape: mvShape(t.shape),
            ...(t.extraShapes ? { extraShapes: t.extraShapes.map(mvShape) } : {}),
        })),
    }, texte, opts(label));
    return meta(payload, key, params);
};

const tachePoint = (id, P, nom, consigne, extraShapes) => ({
    id, consigne, color: COL_V, pointLabel: nom,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: 0 },
    ...(extraShapes ? { extraShapes } : {}),
});
const tacheRay = (id, nomP, angleDeg, consigne) => ({
    id, kind: 'angle_ray', consigne, color: COL_B, tolerance_deg: 2,
    shape: { type: 'ray', from: nomP, angleDeg: ((angleDeg % 360) + 360) % 360 },
});
const tacheArc = (id, P, rCm, consigne) => ({
    id, consigne, color: COL_B,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: rCm * PX },
});

/** Les quatre côtés, tracés automatiquement dès que le dernier sommet est posé. */
const cotesAuto = (A, B, C, D) => [
    { type: 'segment', p1: A, p2: B, color: COL_V },
    { type: 'segment', p1: B, p2: C, color: COL_V },
    { type: 'segment', p1: C, p2: D, color: COL_V },
    { type: 'segment', p1: D, p2: A, color: COL_V },
];

const RAPPEL = `<p><em>Chaque sommet est juste s'il tombe à moins de ${TOL_TXT} de sa position, et `
    + 'chaque étape est comptée séparément. Les quatre côtés se tracent seuls dès que le dernier '
    + 'sommet est posé.</em></p>';

// ═══════════════════════════════════════════════════════════
// CONFIGURATIONS DE CODAGE (le crible de l'identification)
// ═══════════════════════════════════════════════════════════

/**
 * Dix configurations, dont QUATRE insuffisantes
 * (`nombre_de_configurations_insuffisantes`). La nature est posée ICI, à la
 * génération, à partir de la configuration — jamais recalculée à partir de
 * coordonnées flottantes (exclusion 2, vigilance 2).
 */
const CONFIGURATIONS = [
    { id: 'diag_milieu', codage: ['demiDiag'], nature: 'parallélogramme', propriete: 'les diagonales se coupent en leur milieu' },
    { id: 'diag_milieu_egales', codage: ['quatreDemiDiag'], nature: 'rectangle', propriete: 'les diagonales se coupent en leur milieu et ont la même longueur' },
    { id: 'diag_milieu_perp', codage: ['demiDiag', 'perp'], nature: 'losange', propriete: 'les diagonales se coupent en leur milieu et sont perpendiculaires' },
    { id: 'diag_milieu_egales_perp', codage: ['quatreDemiDiag', 'perp'], nature: 'carré', propriete: 'les diagonales se coupent en leur milieu, ont la même longueur et sont perpendiculaires' },
    { id: 'cotes_opposes_egaux', codage: ['cotesOpposes'], nature: 'parallélogramme', propriete: 'les côtés opposés sont de même longueur deux à deux' },
    { id: 'quatre_cotes_egaux', codage: ['quatreCotes'], nature: 'losange', propriete: 'les quatre côtés ont la même longueur' },
    { id: 'deux_cotes_paralleles', codage: ['unParallelisme'], nature: null, contre: 'trapeze', code: E_UNE_EGALITE },
    { id: 'consecutifs_egaux', codage: ['consecutifs'], nature: null, contre: 'cerfVolant', code: E_CONSECUTIFS },
    { id: 'deux_cotes_opposes_egaux', codage: ['deuxOpposes'], nature: null, contre: 'quelconque', code: E_UNE_EGALITE },
    { id: 'diag_perp_seules', codage: ['perp'], nature: null, contre: 'cerfVolant', code: E_PERP_VUE },
];

const SUFFISANTES = CONFIGURATIONS.filter((c) => c.nature);
const INSUFFISANTES = CONFIGURATIONS.filter((c) => !c.nature);

// ═══════════════════════════════════════════════════════════
// FIGURES À MAIN LEVÉE
// ═══════════════════════════════════════════════════════════

const VB_FIG = { w: 260, h: 200 };

const ticksAt = (P, Q, t, n, couleur = '#0f172a') => {
    const cx0 = P.x + t * (Q.x - P.x);
    const cy0 = P.y + t * (Q.y - P.y);
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        const s = (i - (n - 1) / 2) * 4.5;
        const cx = cx0 + ux * s;
        const cy = cy0 + uy * s;
        out += `<line x1="${(cx + uy * 5).toFixed(1)}" y1="${(cy - ux * 5).toFixed(1)}" x2="${(cx - uy * 5).toFixed(1)}" y2="${(cy + ux * 5).toFixed(1)}" stroke="${couleur}" stroke-width="1.6"/>`;
    }
    return out;
};

const chevronsAt = (P, Q, n) => {
    const mx = (P.x + Q.x) / 2;
    const my = (P.y + Q.y) / 2;
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        // Un chevron mesure 8 px de long (de cx - 4 a cx + 4). A 6 px d'ecart, deux
        // chevrons consecutifs se CHEVAUCHAIENT de 2 px : au lieu de deux fleches on
        // lisait une croix emmelee (retour prof 2026-09-27). A 11 px il reste 3 px
        // de blanc entre les deux.
        const s = (i - (n - 1) / 2) * 11;
        const cx = mx + ux * s;
        const cy = my + uy * s;
        out += `<path d="M ${(cx - ux * 4 + uy * 5).toFixed(1)},${(cy - uy * 4 - ux * 5).toFixed(1)} L ${(cx + ux * 4).toFixed(1)},${(cy + uy * 4).toFixed(1)} L ${(cx - ux * 4 - uy * 5).toFixed(1)},${(cy - uy * 4 + ux * 5).toFixed(1)}" fill="none" stroke="#1E3A8A" stroke-width="1.6"/>`;
    }
    return out;
};

const angleDroit = (V, P, Q, c = 11) => {
    const u = (T) => {
        const dx = T.x - V.x;
        const dy = T.y - V.y;
        const L = Math.hypot(dx, dy) || 1;
        return { x: (dx / L) * c, y: (dy / L) * c };
    };
    const a = u(P);
    const b = u(Q);
    return `<path d="M ${(V.x + a.x).toFixed(1)},${(V.y + a.y).toFixed(1)} L ${(V.x + a.x + b.x).toFixed(1)},${(V.y + a.y + b.y).toFixed(1)} L ${(V.x + b.x).toFixed(1)},${(V.y + b.y).toFixed(1)}" fill="none" stroke="#E24B4A" stroke-width="1.6"/>`;
};

/**
 * Schéma À MAIN LEVÉE d'un quadrilatère (exclusion 5) : les sommets sont
 * déformés de quelques pixels, l'allure ne dit donc rien de la nature, et
 * toute la conclusion est portée par le codage.
 */
/**
 * Legende du codage de parallelisme, servie SEULEMENT quand la figure en porte.
 *
 * Le chevron est repandu dans les manuels, mais rien dans le programme ne le
 * nomme, et la specification de ce generateur ecrit meme une autre convention
 * (Â« traits doubles pour le parallelisme Â»). Un codage dont on n'est pas sur
 * qu'il soit connu ne doit pas porter SEUL la reponse â et ici il la porte : c'est
 * le chevron qui distingue le trapeze du parallelogramme. On le dit donc en clair
 * (arbitrage prof 2026-09-27). L'eleve qui connait le code n'est pas gene, celui
 * qui ne le connait pas n'est plus bloque.
 */
// Ici, SEUL `unParallelisme` dessine des chevrons : `cotesOpposes` pose des traits
// d'egalite, qui parlent de longueur et non de parallelisme. Au gen 968 c'est
// l'inverse, d'ou deux predicats distincts.
const legendeChevrons = (codage) => ((codage || []).some((c) => c === 'unParallelisme')
    ? '<p><i>Les chevrons (les petites flèches) marquent des côtés parallèles : deux côtés qui portent le même nombre de chevrons sont parallèles entre eux.</i></p>'
    : '');

const figCodee = (codage, noms, jitter) => {
    const base = [{ x: 30, y: 155 }, { x: 150, y: 175 }, { x: 228, y: 55 }, { x: 108, y: 35 }];
    const S = base.map((p, i) => ({ x: p.x + jitter[i][0], y: p.y + jitter[i][1] }));
    const O = { x: (S[0].x + S[2].x) / 2, y: (S[0].y + S[2].y) / 2 };
    const O2 = { x: (S[1].x + S[3].x) / 2, y: (S[1].y + S[3].y) / 2 };
    const C = { x: (O.x + O2.x) / 2, y: (O.y + O2.y) / 2 };
    let g = `<polygon points="${S.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}" fill="#f8fafc" stroke="#334155" stroke-width="2"/>`;
    const besoinDiagonales = codage.some((c) => ['demiDiag', 'quatreDemiDiag', 'perp'].includes(c));
    if (besoinDiagonales) {
        g += `<line x1="${S[0].x.toFixed(1)}" y1="${S[0].y.toFixed(1)}" x2="${S[2].x.toFixed(1)}" y2="${S[2].y.toFixed(1)}" stroke="#64748b" stroke-width="1.4"/>`;
        g += `<line x1="${S[1].x.toFixed(1)}" y1="${S[1].y.toFixed(1)}" x2="${S[3].x.toFixed(1)}" y2="${S[3].y.toFixed(1)}" stroke="#64748b" stroke-width="1.4"/>`;
    }
    codage.forEach((c) => {
        if (c === 'demiDiag') {
            // Les deux diagonales sont coupees en leur milieu, mais elles n'ont PAS la
            // meme longueur : une marque par diagonale, donc un trait d'un cote, deux
            // de l'autre.
            g += ticksAt(S[0], C, 0.5, 1) + ticksAt(C, S[2], 0.5, 1)
                + ticksAt(S[1], C, 0.5, 2) + ticksAt(C, S[3], 0.5, 2);
        } else if (c === 'quatreDemiDiag') {
            // LE MEME trait sur les quatre demi-diagonales. Quatre demi-diagonales
            // egales disent d'un coup que les diagonales se coupent en leur milieu ET
            // qu'elles ont la meme longueur.
            //
            // Avant, ces deux faits etaient codes separement : un trait / deux traits
            // sur les moities, plus trois traits violets sur chaque diagonale entiere.
            // Ces derniers etaient poses a 14 % de la diagonale, donc SUR la moitie qui
            // portait deja sa propre marque : deux codages d'egalite differents sur le
            // meme bout de segment, sans rien qui dise que l'un parle de la moitie et
            // l'autre de la diagonale entiere (arbitrage prof 2026-09-27).
            g += ticksAt(S[0], C, 0.5, 1) + ticksAt(C, S[2], 0.5, 1)
                + ticksAt(S[1], C, 0.5, 1) + ticksAt(C, S[3], 0.5, 1);
        } else if (c === 'perp') {
            g += angleDroit(C, S[0], S[1]);
        } else if (c === 'cotesOpposes') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[3], S[2], 0.5, 1)
                + ticksAt(S[1], S[2], 0.5, 2) + ticksAt(S[0], S[3], 0.5, 2);
        } else if (c === 'quatreCotes') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[1], S[2], 0.5, 1)
                + ticksAt(S[2], S[3], 0.5, 1) + ticksAt(S[3], S[0], 0.5, 1);
        } else if (c === 'unParallelisme') {
            g += chevronsAt(S[0], S[1], 1) + chevronsAt(S[3], S[2], 1);
        } else if (c === 'consecutifs') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[0], S[3], 0.5, 1)
                + ticksAt(S[1], S[2], 0.5, 2) + ticksAt(S[2], S[3], 0.5, 2);
        } else if (c === 'deuxOpposes') {
            g += ticksAt(S[0], S[1], 0.5, 1) + ticksAt(S[3], S[2], 0.5, 1);
        }
    });
    const pos = [[-12, 12], [12, 14], [12, -8], [-12, -8]];
    S.forEach((p, i) => {
        g += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.4" fill="#0f172a"/>`
            + `<text x="${(p.x + pos[i][0]).toFixed(1)}" y="${(p.y + pos[i][1]).toFixed(1)}" font-family="serif" font-size="14" font-weight="600" text-anchor="middle">${noms[i]}</text>`;
    });
    const s = `${noms.join('')}_${codage.join('-')}_${jitter.flat().join('')}`;
    return `<svg data-v="${s}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_FIG.w} ${VB_FIG.h}" width="${VB_FIG.w}" height="${VB_FIG.h}">`
        + `${g}</svg>`;
};

const tirerJitter = () => [0, 1, 2, 3].map(() => [randInt(-6, 6), randInt(-6, 6)]);

const imgEnonce = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'small' } }],
});

// ═══════════════════════════════════════════════════════════
// Carte 1 — codage (niveau 1)
// ═══════════════════════════════════════════════════════════

const NATURES = ['parallélogramme', 'rectangle', 'losange', 'carré'];

const genCodageQcm = () => {
    const noms = pickLetters(4);
    const cfg = randomChoice(CONFIGURATIONS);
    const options = NATURES.map((nat) => ({
        choix: `C'est un ${nat}.`,
        code: cfg.nature === nat ? null
            : (NATURES.indexOf(nat) > NATURES.indexOf(cfg.nature) ? E_TROP : E_PAS_ASSEZ),
    }));
    options.push({ choix: CONCLURE_IMPOSSIBLE, code: cfg.nature ? E_INSUFFISANT : null });
    const correct = cfg.nature ? `C'est un ${cfg.nature}.` : CONCLURE_IMPOSSIBLE;
    const texte = `<p>Le schéma du quadrilatère ${noms.join('')} est tracé <b>à main levée</b> : `
        + 'l\'allure ne dit rien, seul le codage compte. Quelle est la nature la plus précise que ce '
        + 'codage permet d\'affirmer ?</p>'
        + legendeChevrons(cfg.codage);
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_CODAGE),
        elements_additionnels: imgEnonce(figCodee(cfg.codage, noms, tirerJitter())),
    });
    return meta(payload, 'codage_qcm_unique', {
        niveau: 1,
        usage: 'donner la nature',
        configuration: cfg.id,
        nature_attendue: cfg.nature || 'on ne peut pas conclure',
        suffisante: !!cfg.nature,
        nombre_de_proprietes_mobilisees_par_question: 1,
        nombre_de_quadrilateres_proposes_en_identification: NATURES.length,
        distracteurs: options.filter((o) => o.code),
        erreursVisees: [E_PERP_VUE],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — rectangle_diag (niveau 2) : sans figure
// ═══════════════════════════════════════════════════════════

const genRectangleDiag = () => {
    const n = pickLetters(4);
    const nom = n.join('');
    const [d1, d2] = randomChoice(COUPLES_DIAGONALES);
    const ang = tirerAngleDiagonales();
    const scenarios = [
        {
            id: 'milieu_egales',
            desc: `ses diagonales se coupent en leur milieu et ont la même longueur, ${fr(d1)} cm chacune`,
            reponse: 'oui',
        },
        {
            id: 'milieu_longueurs_differentes',
            desc: `ses diagonales se coupent en leur milieu et mesurent ${fr(d1)} cm et ${fr(d2)} cm`,
            reponse: 'non',
        },
        {
            id: 'egales_sans_milieu',
            desc: `ses diagonales ont la même longueur, ${fr(d1)} cm chacune`,
            reponse: 'conclure',
        },
        {
            id: 'milieu_angle_proche_droit',
            desc: `ses diagonales se coupent en leur milieu en formant un angle de ${ang}°, et mesurent ${fr(d1)} cm et ${fr(d2)} cm`,
            reponse: 'non',
        },
    ];
    const sc = randomChoice(scenarios);
    const options = [
        {
            choix: `Oui : des diagonales qui se coupent en leur milieu et qui ont la même longueur caractérisent un rectangle.`,
            code: sc.reponse === 'oui' ? null : E_TROP,
        },
        {
            choix: `Non : ${nom} est bien un parallélogramme, mais ses diagonales n'ont pas la même longueur, ce n'est donc pas un rectangle.`,
            code: sc.reponse === 'non' ? null : E_PAS_ASSEZ,
        },
        {
            choix: `On ne peut pas conclure : rien ne dit que les diagonales de ${nom} se coupent en leur milieu, ce n'est donc pas forcément un parallélogramme.`,
            code: sc.reponse === 'conclure' ? null : E_INSUFFISANT,
        },
        {
            choix: `Non : ce n'est pas un rectangle car ses diagonales ne sont pas perpendiculaires.`,
            code: E_PERP_VUE,
        },
    ];
    const bonne = options.find((o) => o.code === null);
    const texte = `<p>Le quadrilatère non croisé ${nom} est tel que ${sc.desc}. `
        + `Est-ce un rectangle ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), bonne.choix, texte, opts(L_RECT));
    return meta(payload, 'rectangle_diag_qcm_unique', {
        niveau: 2,
        usage: 'donner la nature',
        scenario: sc.id,
        nature_attendue: sc.reponse,
        nombre_de_proprietes_mobilisees_par_question: 1,
        longueur_d_une_diagonale: [d1, d2],
        angle_entre_les_deux_diagonales: ang,
        distracteurs: options.filter((o) => o.code),
        erreursVisees: [E_PERP_VUE],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — suffisant (niveau 3) : le contre-exemple dessiné
// ═══════════════════════════════════════════════════════════

const P = (x, y) => ({ x, y });
const BRUT_CONTRE = {
    trapeze: {
        pts: [P(20, 140), P(175, 140), P(110, 50), P(45, 50)],
        codage: (s) => chevronsAt(s[0], s[1], 1) + chevronsAt(s[3], s[2], 1),
    },
    cerfVolant: {
        pts: [P(98, 20), P(148, 62), P(98, 148), P(48, 62)],
        codage: (s) => ticksAt(s[0], s[1], 0.5, 1) + ticksAt(s[0], s[3], 0.5, 1)
            + ticksAt(s[1], s[2], 0.5, 2) + ticksAt(s[2], s[3], 0.5, 2),
    },
    quelconque: {
        pts: [P(22, 140), P(107, 140), P(124, 81), P(73, 13)],
        codage: (s) => ticksAt(s[0], s[1], 0.5, 1) + ticksAt(s[2], s[3], 0.5, 1),
    },
    parallelogramme: {
        pts: [P(25, 140), P(120, 140), P(160, 55), P(65, 55)],
        codage: (s) => chevronsAt(s[0], s[1], 1) + chevronsAt(s[3], s[2], 1)
            + ticksAt(s[0], s[1], 0.5, 1) + ticksAt(s[3], s[2], 0.5, 1),
    },
};

/** Rotation puis cadrage : une similitude conserve parallélismes et égalités. */
const posee = (nom, deg) => {
    const pts = BRUT_CONTRE[nom].pts;
    const c = Math.cos(toRad(deg));
    const s = Math.sin(toRad(deg));
    const gx = pts.reduce((a, p) => a + p.x, 0) / 4;
    const gy = pts.reduce((a, p) => a + p.y, 0) / 4;
    const t = pts.map((p) => ({
        x: gx + (p.x - gx) * c - (p.y - gy) * s,
        y: gy + (p.x - gx) * s + (p.y - gy) * c,
    }));
    const xs = t.map((p) => p.x);
    const ys = t.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const k = Math.min((VB_FIG.w - 30) / (Math.max(...xs) - minX), (VB_FIG.h - 30) / (Math.max(...ys) - minY));
    const offX = (VB_FIG.w - k * (Math.max(...xs) - minX)) / 2;
    const offY = (VB_FIG.h - k * (Math.max(...ys) - minY)) / 2;
    return t.map((p) => ({
        x: Math.round((offX + (p.x - minX) * k) * 10) / 10,
        y: Math.round((offY + (p.y - minY) * k) * 10) / 10,
    }));
};

const choixContre = (nom, deg) => {
    const pts = posee(nom, deg);
    const svg = `<svg data-v="${nom}_${deg}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_FIG.w} ${VB_FIG.h}" width="${VB_FIG.w}" height="${VB_FIG.h}">`
        + `<polygon points="${pts.map((p) => `${p.x},${p.y}`).join(' ')}" fill="#f8fafc" stroke="#334155" stroke-width="2"/>`
        + `${BRUT_CONTRE[nom].codage(pts)}</svg>`;
    return { type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } };
};

const CODE_CONTRE = {
    trapeze: E_UNE_EGALITE,
    cerfVolant: E_CONSECUTIFS,
    quelconque: E_UNE_EGALITE,
    parallelogramme: E_INSUFFISANT,
};

const LIBELLE_CODAGE = {
    unParallelisme: 'deux côtés opposés sont marqués parallèles',
    consecutifs: 'les côtés consécutifs sont marqués égaux deux à deux',
    deuxOpposes: 'deux côtés opposés sont marqués de même longueur',
    perp: 'les diagonales sont marquées perpendiculaires, sans autre codage',
};

const genSuffisantQcm = () => {
    const n = pickLetters(4);
    const cfg = randomChoice(INSUFFISANTES);
    const noms = ['trapeze', 'cerfVolant', 'quelconque', 'parallelogramme'];
    const options = noms.map((nom) => ({
        nom,
        choix: choixContre(nom, randInt(-6, 6) * 5),
        code: nom === cfg.contre ? null : CODE_CONTRE[nom],
    }));
    const correct = options.find((o) => o.nom === cfg.contre).choix;
    const texte = `<p>Sur le schéma de ${n.join('')}, ${LIBELLE_CODAGE[cfg.codage[0]]}. Ce codage `
        + '<b>ne suffit pas</b> pour affirmer que c\'est un parallélogramme — attention, cela ne veut '
        + 'pas dire que ce n\'en est pas un, seulement qu\'on ne peut pas l\'affirmer.</p>'
        + '<p>Quelle figure le montre ? (Elle porte le même codage, et ce n\'est pas un '
        + 'parallélogramme.)</p>';
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, opts(L_SUFFISANT));
    return meta(payload, 'suffisant_qcm_unique', {
        niveau: 3,
        usage: 'donner la nature',
        configuration: cfg.id,
        contre_exemple: cfg.contre,
        suffisante: false,
        nombre_de_proprietes_mobilisees_par_question: 1,
        distracteurs: options.filter((o) => o.code).map((o) => ({ choix: o.nom, code: o.code })),
        erreursVisees: [cfg.code, E_INSUFFISANT],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — construire_diag (niveau 4) : deux diagonales et leur angle
// ═══════════════════════════════════════════════════════════

/** Le parallélogramme défini par ses deux diagonales et leur angle. */
const paraParDiagonales = (d1, d2, ang, theta) => {
    const O = { x: 0, y: 0 };
    const demi = (d, a) => ({
        x: (d / 2) * PX * Math.cos(toRad(a)),
        y: -(d / 2) * PX * Math.sin(toRad(a)),
    });
    const u = demi(d1, theta);
    const v = demi(d2, theta + ang);
    return {
        A: { x: O.x - u.x, y: O.y - u.y },
        C: { x: O.x + u.x, y: O.y + u.y },
        B: { x: O.x + v.x, y: O.y + v.y },
        D: { x: O.x - v.x, y: O.y - v.y },
        O,
    };
};

const genConstruireDiagPC = () => {
    const [d1, d2, rapport] = randomChoice(COUPLES_DIAGONALES);
    const ang = tirerAngleDiagonales();
    const theta = randInt(0, 35) * 10;
    const [nA, nB, nC, nD] = pickLetters(4);
    const p = paraParDiagonales(d1, d2, ang, theta);
    return assembler({
        // Le centre O est DONNE, et non place par l'eleve. Le rapporteur du composant
        // resout son sommet dans la liste des points donnes seulement
        // (ProgrammeConstructionEleve : `points.find(p => p.name === exp.from)`, sinon
        // il ne se monte pas). Tant que O etait construit par l'eleve, le pas « place
        // le rapporteur sur O » n'affichait aucun rapporteur et l'interface retombait
        // sur le geste du compas (retour prof 2026-09-26).
        // Ce qu'on perd — construire le milieu d'une diagonale — reste travaille par
        // la carte « construire_angles », qui le fait placer sans rapporteur.
        points: [{ name: nA, ...p.A }, { name: nC, ...p.C }, { name: NOM_CENTRE, ...p.O }],
        preShapes: [{ type: 'line', from: nA, to: nC }],
        tasks: [
            tacheRay('ray_O', NOM_CENTRE, theta + ang,
                `Place le rapporteur sur ${NOM_CENTRE}, aligne sa base sur [${NOM_CENTRE}${nC}), puis clique à <b>${ang}°</b> : c'est la direction de la seconde diagonale.`),
            tachePoint('point_B', p.B, nB,
                `Place ${nB} sur cette demi-droite, à <b>${fr(d2 / 2)} cm</b> de ${NOM_CENTRE} — la MOITIÉ de la diagonale [${nB}${nD}].`),
            tachePoint('point_D', p.D, nD,
                `Place ${nD}, symétrique de ${nB} par rapport à ${NOM_CENTRE}, à <b>${fr(d2 / 2)} cm</b> de l'autre côté.`,
                cotesAuto(p.A, p.B, p.C, p.D)),
        ],
        texte: `<p>Construis le quadrilatère ${nA}${nB}${nC}${nD} dont les diagonales [${nA}${nC}] et `
            + `[${nB}${nD}] mesurent <b>${fr(d1)} cm</b> et <b>${fr(d2)} cm</b>, se coupent en leur `
            + `<b>milieu</b> et forment un angle de <b>${ang}°</b>. La diagonale [${nA}${nC}] est déjà `
            + `tracée, et son milieu ${NOM_CENTRE} est marqué.</p>`
            + `<p>Propriété employée : un quadrilatère non croisé dont les diagonales se coupent en `
            + `leur milieu est un parallélogramme. Ses diagonales n'ont pas la même longueur et leur `
            + `angle n'est pas droit : ce n'est donc ni un rectangle, ni un losange, ni un carré.</p>`
            + RAPPEL,
        label: L_DIAG,
        key: 'construire_diag_programme_construction',
        params: {
            niveau: 4,
            usage: 'construire',
            jeu_de_donnees: 'deux diagonales et leur angle',
            propriete: 'les diagonales se coupent en leur milieu',
            nature_attendue: 'parallélogramme',
            nombre_de_proprietes_mobilisees_par_question: 1,
            longueur_d_une_diagonale: [d1, d2],
            rapport_des_deux_diagonales: rapport,
            angle_entre_les_deux_diagonales: ang,
            orientation: theta,
            erreursVisees: [E_DEMI_DIAG, E_SANS_QUALIFIER],
        },
    });
};

const genConstruireDiagRelier = () => {
    const [d1, d2] = randomChoice(COUPLES_DIAGONALES);
    const [e1] = randomChoice(COUPLES_DIAGONALES);
    const pairs = shuffle([
        {
            source: `Elles se coupent en leur milieu, mesurent ${fr(d1)} cm et ${fr(d2)} cm, et ne sont pas perpendiculaires`,
            target: 'Parallélogramme, sans plus de précision',
        },
        {
            source: `Elles se coupent en leur milieu, mesurent ${fr(e1)} cm chacune, et ne sont pas perpendiculaires`,
            target: 'Rectangle',
        },
        {
            source: `Elles se coupent en leur milieu, sont perpendiculaires, et mesurent ${fr(d1)} cm et ${fr(d2)} cm`,
            target: 'Losange',
        },
        {
            source: `Elles se coupent en leur milieu, sont perpendiculaires, et mesurent ${fr(e1)} cm chacune`,
            target: 'Carré',
        },
    ]);
    const texte = '<p>On construit un quadrilatère à partir de ses deux diagonales. Relie chaque jeu '
        + 'de propriétés des diagonales à la nature <b>la plus précise</b> du quadrilatère obtenu.</p>';
    const payload = makeRelier(pairs, texte, opts(L_DIAG));
    return meta(payload, 'construire_diag_relier', {
        niveau: 4,
        usage: 'donner la nature',
        nombre_de_proprietes_mobilisees_par_question: 1,
        longueur_d_une_diagonale: [d1, d2, e1],
        erreursVisees: [E_TROP, E_PAS_ASSEZ],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — cotes_angle (niveau 5)
// ═══════════════════════════════════════════════════════════

const genCotesAnglePC = () => {
    const [c1, c2] = tirerCotes();
    const alpha = randInt(ANGLE_SOMMET_MIN, ANGLE_SOMMET_MAX);
    const theta = randInt(0, 35) * 10;
    const [nA, nB, nC, nD] = pickLetters(4);
    const A = { x: 0, y: 0 };
    const B = { x: c1 * PX * Math.cos(toRad(theta)), y: -c1 * PX * Math.sin(toRad(theta)) };
    const v = {
        x: c2 * PX * Math.cos(toRad(theta + alpha)),
        y: -c2 * PX * Math.sin(toRad(theta + alpha)),
    };
    const D = { x: A.x + v.x, y: A.y + v.y };
    const C = { x: B.x + v.x, y: B.y + v.y };
    return assembler({
        points: [{ name: nA, ...A }, { name: nB, ...B }],
        preShapes: [{ type: 'line', from: nA, to: nB }],
        tasks: [
            tacheRay('ray_A', nA, theta + alpha,
                `Place le rapporteur sur ${nA}, aligne sa base sur [${nA}${nB}), puis clique à <b>${alpha}°</b>.`),
            tachePoint('point_D', D, nD,
                `Place ${nD} sur cette demi-droite, à <b>${c2} cm</b> de ${nA}.`),
            tachePoint('point_C', C, nC,
                `Place ${nC} : les côtés opposés d'un parallélogramme sont parallèles et de même longueur, donc on passe de ${nD} à ${nC} comme de ${nA} à ${nB}.`,
                cotesAuto(A, B, C, D)),
        ],
        texte: `<p>Construis le parallélogramme ${nA}${nB}${nC}${nD} tel que ${nA}${nB} = <b>${c1} cm</b>, `
            + `${nA}${nD} = <b>${c2} cm</b> et l'angle de sommet ${nA} mesure <b>${alpha}°</b>. `
            + `Le côté [${nA}${nB}] est déjà tracé.</p>`
            + `<p>Propriété employée : un quadrilatère non croisé dont les côtés opposés sont `
            + `parallèles et de même longueur deux à deux est un parallélogramme. L'angle en ${nA} `
            + `est obtus, donc ce n'est pas un rectangle ; les deux côtés sont de longueurs `
            + `différentes, donc ce n'est pas un losange.</p>` + RAPPEL,
        label: L_COTES,
        key: 'cotes_angle_programme_construction',
        params: {
            niveau: 5,
            usage: 'construire',
            jeu_de_donnees: 'deux cotes et un angle au sommet',
            propriete: 'les cotes opposes sont paralleles et de meme longueur',
            nature_attendue: 'parallélogramme',
            nombre_de_proprietes_mobilisees_par_question: 1,
            longueur_d_un_cote: [c1, c2],
            angle_au_sommet: alpha,
            orientation: theta,
            erreursVisees: [E_SANS_QUALIFIER],
        },
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — demi_diagonales (niveau 6)
// ═══════════════════════════════════════════════════════════

const genDemiDiagonalesPC = () => {
    for (let essai = 0; essai < 300; essai++) {
        const [d1, d2, rapport] = randomChoice(COUPLES_DIAGONALES);
        const cote = randInt(COTE_MIN, COTE_MAX);
        const r1 = d1 / 2;
        const r2 = d2 / 2;
        // Le triangle (côté, demi-diagonale, demi-diagonale) doit exister, sans être plat.
        if (r1 + r2 <= cote + 0.5 || Math.abs(r1 - r2) >= cote - 0.5) continue;
        const theta = randInt(0, 35) * 10;
        const [nA, nB, nC, nD] = pickLetters(4);
        const A = { x: 0, y: 0 };
        const B = { x: cote * PX * Math.cos(toRad(theta)), y: -cote * PX * Math.sin(toRad(theta)) };
        // O : intersection des deux arcs, du côté « au-dessus » de [AB].
        const dAB = cote * PX;
        const x = ((r1 * PX) ** 2 - (r2 * PX) ** 2 + dAB ** 2) / (2 * dAB);
        const h2 = (r1 * PX) ** 2 - x ** 2;
        if (h2 <= 0) continue;
        const h = Math.sqrt(h2);
        const ux = (B.x - A.x) / dAB;
        const uy = (B.y - A.y) / dAB;
        const O = { x: A.x + ux * x + uy * h, y: A.y + uy * x - ux * h };
        const C = { x: 2 * O.x - A.x, y: 2 * O.y - A.y };
        const D = { x: 2 * O.x - B.x, y: 2 * O.y - B.y };
        return assembler({
            points: [{ name: nA, ...A }, { name: nB, ...B }],
            preShapes: [{ type: 'line', from: nA, to: nB }],
            tasks: [
                tacheArc('arc_A', A, r1,
                    `Trace en <b>bleu</b> l'arc de centre ${nA} et de rayon <b>${fr(r1)} cm</b>, la MOITIÉ de la diagonale [${nA}${nC}].`),
                tacheArc('arc_B', B, r2,
                    `Trace en <b>bleu</b> l'arc de centre ${nB} et de rayon <b>${fr(r2)} cm</b>, la MOITIÉ de la diagonale [${nB}${nD}].`),
                tachePoint('point_O', O, NOM_CENTRE,
                    `Place le centre ${NOM_CENTRE} à l'intersection des deux arcs, au-dessus de [${nA}${nB}].`),
                tachePoint('point_C', C, nC,
                    `Place ${nC}, symétrique de ${nA} par rapport à ${NOM_CENTRE}.`),
                tachePoint('point_D', D, nD,
                    `Place ${nD}, symétrique de ${nB} par rapport à ${NOM_CENTRE}.`,
                    cotesAuto(A, B, C, D)),
            ],
            texte: `<p>Construis le parallélogramme ${nA}${nB}${nC}${nD} tel que ${nA}${nB} = `
                + `<b>${cote} cm</b>, et dont les diagonales [${nA}${nC}] et [${nB}${nD}] mesurent `
                + `<b>${fr(d1)} cm</b> et <b>${fr(d2)} cm</b>. Le côté [${nA}${nB}] est déjà tracé.</p>`
                + `<p>Propriété employée : les diagonales se coupent en leur <b>milieu</b>. Ce sont `
                + `donc les <b>demi</b>-diagonales, ${fr(r1)} cm et ${fr(r2)} cm, qu'il faut reporter `
                + `au compas depuis ${nA} et ${nB}, et non les diagonales entières.</p>` + RAPPEL,
            label: L_DEMI,
            key: 'demi_diagonales_programme_construction',
            params: {
                niveau: 6,
                usage: 'construire',
                jeu_de_donnees: 'un cote et les deux demi-diagonales',
                propriete: 'les diagonales se coupent en leur milieu',
                nature_attendue: 'parallélogramme',
                nombre_de_proprietes_mobilisees_par_question: 1,
                longueur_d_une_diagonale: [d1, d2],
                rapport_des_deux_diagonales: rapport,
                longueur_d_un_cote: [cote],
                orientation: theta,
                erreursVisees: [E_DEMI_DIAG, E_SANS_QUALIFIER],
            },
        });
    }
    return genConstruireDiagPC();
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — alternes (niveau 7) : une diagonale et deux angles
// ═══════════════════════════════════════════════════════════

const genAlternesPC = () => {
    const [d1] = randomChoice(COUPLES_DIAGONALES);
    const [a1, a2] = tirerAnglesDiagonale();
    const theta = randInt(0, 35) * 10;
    const [nA, nB, nC, nD] = pickLetters(4);
    const A = { x: 0, y: 0 };
    const C = { x: d1 * PX * Math.cos(toRad(theta)), y: -d1 * PX * Math.sin(toRad(theta)) };
    const O = { x: (A.x + C.x) / 2, y: (A.y + C.y) / 2 };
    // B : intersection de la demi-droite issue de A à a1 et de celle issue de C à a2.
    const L = (d1 * PX * Math.sin(toRad(a2))) / Math.sin(toRad(a1 + a2));
    const B = {
        x: A.x + L * Math.cos(toRad(theta + a1)),
        y: A.y - L * Math.sin(toRad(theta + a1)),
    };
    const D = { x: 2 * O.x - B.x, y: 2 * O.y - B.y };
    return assembler({
        points: [{ name: nA, ...A }, { name: nC, ...C }],
        preShapes: [{ type: 'line', from: nA, to: nC }],
        tasks: [
            tacheRay('ray_A', nA, theta + a1,
                `Place le rapporteur sur ${nA}, aligne sa base sur [${nA}${nC}), puis clique à <b>${a1}°</b>.`),
            tacheRay('ray_C', nC, theta + 180 - a2,
                `Place le rapporteur sur ${nC}, aligne sa base sur [${nC}${nA}), puis clique à <b>${a2}°</b>, du même côté.`),
            tachePoint('point_B', B, nB,
                `Place ${nB} à l'intersection des deux demi-droites.`),
            tachePoint('point_O', O, NOM_CENTRE,
                `Place le centre ${NOM_CENTRE}, milieu de la diagonale [${nA}${nC}].`),
            tachePoint('point_D', D, nD,
                `Place ${nD}, symétrique de ${nB} par rapport à ${NOM_CENTRE}.`,
                cotesAuto(A, B, C, D)),
        ],
        texte: `<p>Construis le parallélogramme ${nA}${nB}${nC}${nD} dont la diagonale [${nA}${nC}] `
            + `mesure <b>${fr(d1)} cm</b>, l'angle entre [${nA}${nC}) et [${nA}${nB}) mesure `
            + `<b>${a1}°</b>, et l'angle entre [${nC}${nA}) et [${nC}${nB}) mesure <b>${a2}°</b>. `
            + `La diagonale [${nA}${nC}] est déjà tracée.</p>`
            + `<p>Propriété employée : dans le parallélogramme ${nA}${nB}${nC}${nD}, les côtés `
            + `[${nA}${nD}] et [${nB}${nC}] sont parallèles, et la diagonale (${nA}${nC}) les coupe. `
            + `Les angles de sommet ${nA} entre [${nA}${nC}) et [${nA}${nD}) d'une part, de sommet `
            + `${nC} entre [${nC}${nA}) et [${nC}${nB}) d'autre part, sont donc <b>alternes internes</b> `
            + `et égaux : ${nD} se trouve en reportant <b>${a2}°</b> en ${nA}, de l'autre côté de `
            + `(${nA}${nC}). Le symétrique par rapport à ${NOM_CENTRE} donne le même point.</p>` + RAPPEL,
        label: L_ALTERNES,
        key: 'alternes_programme_construction',
        params: {
            niveau: 7,
            usage: 'construire',
            jeu_de_donnees: 'une diagonale et deux angles',
            propriete: 'angles alternes internes et symetrie centrale',
            nature_attendue: 'parallélogramme',
            nombre_de_proprietes_mobilisees_par_question: 1,
            longueur_d_une_diagonale: [d1],
            angles_avec_la_diagonale: [a1, a2],
            orientation: theta,
            erreursVisees: [E_SANS_QUALIFIER],
        },
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 8 — dole (niveau 8) : construire puis conclure
// ═══════════════════════════════════════════════════════════

const REPERE = { xMin: 0, yMin: 0, xMax: 10, yMax: 10, xStep: 1, yStep: 1 };

/** Les jambes du triangle rectangle sont TOUJOURS différentes : jamais un carré. */
const tirerDole = () => {
    for (let essai = 0; essai < 300; essai++) {
        const R = { x: randInt(3, 7), y: randInt(3, 7) };
        const a = randInt(2, 4);
        const b = randInt(2, 4);
        if (a === b) continue;                   // sinon DOLE serait un carré
        const sa = randomChoice([1, -1]);
        const sb = randomChoice([1, -1]);
        const D = { x: R.x, y: R.y + sa * a };
        const O = { x: R.x + sb * b, y: R.y };
        const L = { x: 2 * R.x - D.x, y: 2 * R.y - D.y };
        const E = { x: 2 * R.x - O.x, y: 2 * R.y - O.y };
        if ([R, D, O, L, E].every((p) => p.x >= 1 && p.x <= 9 && p.y >= 1 && p.y <= 9)) {
            return { R, D, O, L, E, a, b };
        }
    }
    const R = { x: 5, y: 5 };
    return {
        R, D: { x: 5, y: 8 }, O: { x: 7, y: 5 }, L: { x: 5, y: 2 }, E: { x: 3, y: 5 }, a: 3, b: 2,
    };
};

const genDolePlacer = () => {
    const [nD, nR, nO, nL, nE] = pickLetters(5);
    const t = tirerDole();
    // L'enonce annoncait « tu donneras ensuite la nature du quadrilatere DOLE : on
    // construit, puis on conclut », alors que ce format ne recueille QUE les deux
    // points : placer_points_2d n'accepte pas d'autre interaction. Le « ensuite »
    // renvoyait au format dole_texte_trou_drag, servi independamment — un eleve
    // pouvait donc recevoir celui-ci seul et se voir promettre un travail qui ne
    // venait jamais (retour prof 2026-09-26). La progression « on construit, puis on
    // conclut » reste celle de la CARTE, portee par ses deux formats.
    const texte = `<p>Le triangle ${nD}${nR}${nO} est rectangle en ${nR}. Place les points <b>${nL}</b> `
        + `et <b>${nE}</b>, images respectives de ${nD} et ${nO} par le demi-tour de centre ${nR}.</p>`;
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere: REPERE,
        pointsToPlace: [nL, nE],
        dejaPlaces: [
            { x: t.D.x, y: t.D.y, label: nD },
            { x: t.R.x, y: t.R.y, label: nR },
            { x: t.O.x, y: t.O.y, label: nO },
        ],
        bonne_reponse: JSON.stringify({ [nL]: `${t.L.x};${t.L.y}`, [nE]: `${t.E.x};${t.E.y}` }),
    }, opts(L_DOLE));
    return meta(payload, 'dole_placer_points_2d', {
        niveau: 8,
        usage: 'construire',
        propriete: 'les diagonales se coupent en leur milieu',
        nature_attendue: 'losange',
        nombre_de_proprietes_mobilisees_par_question: 1,
        jambes: [t.a, t.b],
        erreursVisees: [E_SANS_QUALIFIER],
    });
};

const genDoleNature = () => {
    const [nD, nR, nO, nL, nE] = pickLetters(5);
    const t = tirerDole();
    const para = 'un parallélogramme';
    const phrase = `${nR} est le milieu de [${nD}${nL}] et de [${nO}${nE}]. `
        + `Les diagonales [${nD}${nL}] et [${nO}${nE}] du quadrilatère ${nD}${nO}${nL}${nE} ont donc le `
        + `même milieu, ce qui suffit à affirmer que ${nD}${nO}${nL}${nE} est {{${para}}}. `
        + `Comme le triangle ${nD}${nR}${nO} est rectangle en ${nR}, ces diagonales sont `
        + `{{perpendiculaires}}. Un parallélogramme dont les diagonales sont perpendiculaires est `
        + `{{un losange}}. Les deux diagonales mesurent ${2 * t.a} et ${2 * t.b} unités, elles n'ont `
        + `donc pas la même longueur, et ce n'est pas {{un carré}}.`;
    const distract = filtrerDistracteurs(phrase, [
        'un rectangle', 'un trapèze', 'parallèles', 'de même longueur',
    ]);
    const texte = `<p>Le triangle ${nD}${nR}${nO} est rectangle en ${nR} ; ${nL} et ${nE} sont les `
        + `images de ${nD} et ${nO} par le demi-tour de centre ${nR}. Complète pour donner la nature `
        + `la plus précise de ${nD}${nO}${nL}${nE}.</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DOLE), mode: 'drag', distracteurs: distract,
    });
    return meta(payload, 'dole_texte_trou_drag', {
        niveau: 8,
        usage: 'donner la nature',
        propriete: 'les diagonales se coupent en leur milieu',
        nature_attendue: 'losange',
        nombre_de_proprietes_mobilisees_par_question: 1,
        jambes: [t.a, t.b],
        erreursVisees: [E_TROP],
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    codage_qcm_unique: genCodageQcm,
    rectangle_diag_qcm_unique: genRectangleDiag,
    suffisant_qcm_unique: genSuffisantQcm,
    construire_diag_programme_construction: genConstruireDiagPC,
    construire_diag_relier: genConstruireDiagRelier,
    cotes_angle_programme_construction: genCotesAnglePC,
    demi_diagonales_programme_construction: genDemiDiagonalesPC,
    alternes_programme_construction: genAlternesPC,
    dole_placer_points_2d: genDolePlacer,
    dole_texte_trou_drag: genDoleNature,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', placer_points_2d: 'Repère', texte_trou_drag: 'Menus',
    relier: 'Relier', programme_construction: 'Construction',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'codage', label: L_CODAGE, formatsList: buildFormats('codage') },
    { id: 'rectangle_diag', label: L_RECT, formatsList: buildFormats('rectangle_diag') },
    { id: 'suffisant', label: L_SUFFISANT, formatsList: buildFormats('suffisant') },
    { id: 'construire_diag', label: L_DIAG, formatsList: buildFormats('construire_diag') },
    { id: 'cotes_angle', label: L_COTES, formatsList: buildFormats('cotes_angle') },
    { id: 'demi_diagonales', label: L_DEMI, formatsList: buildFormats('demi_diagonales') },
    { id: 'alternes', label: L_ALTERNES, formatsList: buildFormats('alternes') },
    { id: 'dole', label: L_DOLE, formatsList: buildFormats('dole') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    COUPLES_DIAGONALES, CONFIGURATIONS, SUFFISANTES, INSUFFISANTES, BRUT_CONTRE,
    tirerAngleDiagonales, tirerAnglesDiagonale, tirerCotes, tirerDole, posee,
    paraParDiagonales,
};
